import { Box } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';

import { tradingSlice } from '!views/Trading/redux';
import { getSelectedTrade } from '!views/Trading/redux/trading.selectors';
import { TrlFlag } from '»components/atoms';
import { TrlOperationType } from '»components/atoms/TrlOperationType';
import { uuidSelector } from '»components/Auth';
import {
  TrlAccordion,
  TrlDocsModal,
  TrlListContent,
  TrlListTitle,
  TrlNavigationFooter,
} from '»components/molecules';
import { TrlManageOrderButton } from '»components/molecules/TrlManageOrderButton';
import { PrivateView } from '»components/templates';
import { useIsOrderCancellable, useOrdStatusLabel } from '»utils/customHooks';
import { TrlFormattedMessagePlain } from '»utils/i18n';
import { getOperationLabel } from '»utils/trading/trading.utils';
import { TrlFieldDefaultValue } from '»utils/trlFieldDefaultValue';

import {
  CardWrapper,
  DataLabel,
  DataValue,
  FlagContainer,
  InlineContainer,
  ListsWrapper,
  Separator,
  TradeInfo,
  TradeName,
  Wrapper,
  WrapperData,
} from './TradingFundsDetails.styles';

export const TradingFundsDetails = () => {
  const dispatch = useDispatch();
  const location = useLocation();

  const [openFilesModal, setOpenFilesModal] = useState(false);

  const selectedItem = useSelector(getSelectedTrade);
  const uuid = useSelector(uuidSelector);

  const ordBookKey = location.state?.ordBookKey;
  const bookType = location.state?.bookType;

  const { getOrdStatusLabel } = useOrdStatusLabel();

  const operationLabel = getOperationLabel(selectedItem?.side);

  const isOrderCancellable = useIsOrderCancellable({
    revokeDate: selectedItem?.revokeDate,
    ordStatus: selectedItem?.ordStatus,
  });

  useEffect(() => {
    dispatch(
      tradingSlice.actions.tradeEventsRequest({
        ordBookKey: ordBookKey || selectedItem?.ordBookKey,
        bookType: bookType || selectedItem?.bookType,
        market: 'funds',
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uuid]);

  const handleSetFilesModal = () => {
    setOpenFilesModal(!openFilesModal);
  };

  const lastEvent = selectedItem?.events.reduce((acc, currentEvent, index) => {
    if (index === 0 || Number(currentEvent.id) > Number(acc.id))
      return currentEvent;

    return acc;
  }, null);

  const mainInfo = [
    {
      label: 'TRLT_UI-ORDER_NUMBER_SHORT',
      value: ordBookKey || selectedItem.ordBookKey,
    },
    { label: 'TRLT_UI-TICKER', value: selectedItem?.symbol },
    { label: 'TRLT_UI-SECURITY', value: selectedItem?.descr },
    { label: 'TRLT_UI-CURRENCY', value: selectedItem?.currency },
    { label: 'TRLT_UI-MARKET', value: selectedItem?.exchangeDescr },
    { label: 'TRLT_UI-STATUS', value: lastEvent?.ordStatus, type: 'status' },
    {
      label: 'TRLT_UI-SUBSCRIBE_RESCUE',
      value: selectedItem?.side,
      type: 'operation',
    },
    { label: 'TRLT_UI-REQUESTED_QTY', value: selectedItem?.orderQty },
    { label: 'TRLT_UI-EXECUTED_QTY_LONG', value: lastEvent?.cumQty },
    { label: 'TRLT_UI-ACTIVE_QTY_LONG', value: lastEvent?.leavesQty },
    {
      label: 'TRLT_UI-PRICE',
      value: selectedItem?.price,
      type: 'numeric',
      field: 'price',
      priceRepresentation: selectedItem?.priceRepresentation,
      currency: selectedItem?.currency,
      roundPrice: selectedItem?.roundPrice,
    },
    {
      label: 'TRLT_UI-GROSS_VALUE',
      value: selectedItem?.grossAmount,
      type: 'numeric',
      field: 'grossAmount',
      priceRepresentation: selectedItem?.priceRepresentation,
      currency: selectedItem?.currency,
      roundPrice: selectedItem?.roundPrice,
    },
    {
      label: 'TRLT_UI-AVERAGE_PRICE',
      value: selectedItem?.avgPx,
      type: 'numeric',
      field: 'avgPx',
      priceRepresentation: selectedItem?.priceRepresentation,
      currency: selectedItem?.currency,
      roundPrice: selectedItem?.roundPrice,
    },
    {
      label: 'TRLT_UI-CHARGES_VALUE',
      value: selectedItem?.commissions,
      type: 'numeric',
      field: 'commissions',
      currency: selectedItem?.currency,
      roundPrice: selectedItem?.roundPrice,
    },
    {
      label: 'TRLT_UI-NET_VALUE',
      value: selectedItem?.netAmount,
      type: 'numeric',
      field: 'netAmount',
      currency: selectedItem?.currency,
      roundPrice: selectedItem?.roundPrice,
    },
    {
      label: 'TRLT_UI-INSERT_DATE',
      value: selectedItem?.ordDate,
      type: 'datetime',
    },
    {
      label: 'TRLT_UI-EXPIRATION_DATE_LONG',
      value: selectedItem?.expireTime,
      type: 'date',
    },
    {
      label: 'TRLT_UI-PROOF',
      onClick: handleSetFilesModal,
      type: 'openfilesmodal',
    },
  ];

  const displayedEvents = selectedItem?.events.reduce((acc, event, index) => {
    acc.push({
      id: `${event.timestamp}-${index}-${event.grossTradeAmount}`,
      header: [
        {
          value: event.timestamp,
          type: 'datetime',
          color: 'onPrimaryBackground',
        },
        {
          value: event.ordStatus,
          type: 'status',
          textTransform: 'capitalize',
          color: 'highlight',
        },
      ],
      content: [
        {
          label: 'TRLT_UI-DEAL_QTY',
          value: event.lastQty,
        },
        {
          label: 'TRLT_UI-PRICE',
          value: event.lastPx,
        },
        {
          label: 'TRLT_UI-VALUE',
          value: event.grossTradeAmount,
          type: 'numeric',
          field: 'grossTradeAmount',
          priceRepresentation: selectedItem?.priceRepresentation,
          currency: selectedItem?.currency,
          roundPrice: selectedItem?.roundPrice,
        },
      ],
    });

    return acc;
  }, []);

  const [windowSize, setWindowSize] = useState(window.innerHeight);

  const listRef = useRef(null);
  const listsHeight =
    listRef.current && windowSize - listRef.current.offsetTop - 71 - 56;

  const OrdStatusElement = getOrdStatusLabel(selectedItem?.ordStatus, 'pill');

  useEffect(() => {
    const handleResize = () => {
      setWindowSize(window.innerHeight);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <PrivateView>
      <>
        <Wrapper>
          <CardWrapper>
            <Box>
              <InlineContainer>
                <TradeName>{selectedItem?.descr}</TradeName>

                <OrdStatusElement />
              </InlineContainer>
              <TradeInfo>
                <FlagContainer>
                  <TrlFlag flag={selectedItem?.country} />
                  {selectedItem?.exchangeDescr}
                </FlagContainer>
                <Separator />
                {selectedItem?.symbol}
                <Separator />
                {selectedItem?.currency}
              </TradeInfo>
            </Box>

            <InlineContainer>
              <WrapperData>
                <DataLabel>
                  {TrlFormattedMessagePlain({ id: 'TRLT_UI-REQUESTES_QTY' })}
                </DataLabel>
                <DataValue>{selectedItem?.orderQty}</DataValue>
              </WrapperData>

              <WrapperData>
                <DataLabel>
                  {TrlFormattedMessagePlain({ id: 'TRLT_UI-EXECUTED_QTY' })}
                </DataLabel>
                <DataValue>{selectedItem?.cumQty}</DataValue>
              </WrapperData>

              <WrapperData>
                <DataLabel>
                  {TrlFormattedMessagePlain({ id: 'TRLT_UI-ORDER' })}
                </DataLabel>
                <DataValue letters>
                  <TrlFieldDefaultValue />
                </DataValue>
              </WrapperData>

              {operationLabel && selectedItem?.side && (
                <TrlOperationType
                  side={selectedItem?.side}
                  label={operationLabel}
                />
              )}
            </InlineContainer>
          </CardWrapper>

          <ListsWrapper ref={listRef} listsHeight={listsHeight}>
            <TrlListTitle title="TRLT_UI-DETAIL_INFO" />
            <TrlListContent
              data={mainInfo}
              hasMarginBottom={displayedEvents?.length === 0}
            />
            {displayedEvents?.length > 0 && (
              <TrlAccordion
                data={displayedEvents}
                title="TRLT_UI-EVENTS"
                hasMarginTop
              />
            )}
          </ListsWrapper>

          <TrlNavigationFooter>
            <TrlManageOrderButton
              variant="cancel"
              dataItem={selectedItem}
              disabled={!isOrderCancellable}
              isTextButton
            />
          </TrlNavigationFooter>
        </Wrapper>
        <TrlDocsModal open={openFilesModal} onClose={handleSetFilesModal} />
      </>
    </PrivateView>
  );
};
